import { useCallback, useEffect, useRef, useState } from 'react';
import { registerLinkPicker } from '../bridge';
import { RETURN_TYPE } from '../definitions';

const CLOSED = { visible: false, args: {}, openId: 0 };

/**
 * Owns the link picker dialog and hands back a promise-returning opener.
 *
 * Mirrors `useConfirmModal`, which is the CMS's established shape for "a dialog that
 * behaves like a function call". `CmsReactContext` mounts both the same way.
 *
 * The opener is also registered on the module-level bridge so the Froala toolbar button can
 * reach it; see `../bridge.js` for why that cannot be a context read.
 */
const useLinkPicker = () => {
  const [pickerProps, setPickerProps] = useState(CLOSED);
  /*
   * The pending `resolve`, so a second open cannot strand the first caller.
   *
   * Opening replaces `pickerProps` wholesale, including `onClose`, which made the previous
   * promise unreachable: it never settled, and an `await showLinkSearch(...)` in a
   * LinkFinder hung forever with no error. Radix's overlay makes this hard to reach by
   * clicking, but `bridge.showLinkPicker` can be called programmatically at any time.
   */
  const pendingResolve = useRef(null);
  const openCount = useRef(0);

  const settle = useCallback((result) => {
    const resolve = pendingResolve.current;
    pendingResolve.current = null;
    // `null` on dismiss, which is what the form fields and the Froala seam check for.
    if (resolve) resolve(result ?? null);
  }, []);

  const showLinkPicker = useCallback(
    (args = {}) =>
      new Promise((resolve) => {
        // Settle any previous caller as a dismissal before taking the dialog over.
        settle(null);
        pendingResolve.current = resolve;
        openCount.current += 1;
        setPickerProps({
          visible: true,
          args,
          // Changes on every open so CmsReactContext can key the dialog and force a
          // remount, which is what gives it Angular's fresh-scope-per-open behaviour.
          openId: openCount.current,
          onClose: (result) => {
            setPickerProps(CLOSED);
            settle(result);
          },
        });
      }),
    [settle]
  );

  // A provider unmount with the dialog open would otherwise strand the caller too.
  useEffect(() => () => settle(null), [settle]);

  useEffect(() => registerLinkPicker(showLinkPicker), [showLinkPicker]);

  /**
   * The signature Angular's `root_controller` exposed as `showLinkSearch`, kept verbatim
   * so the React form fields that call it did not have to change: positional
   * `(currentHref, allowEmbeddedMenus)`, always `normalizedLink`.
   */
  const showLinkSearch = useCallback(
    (currentHref, allowEmbeddedMenus) =>
      showLinkPicker({
        currentHref,
        returnType: RETURN_TYPE.normalizedLink,
        allowEmbeddedMenus,
      }),
    [showLinkPicker]
  );

  return { showLinkPicker, showLinkSearch, linkPickerProps: pickerProps };
};

export default useLinkPicker;
